import firebase from 'firebase/compat/app';

// Add the Firebase products that you want to use
import "firebase/compat/auth";
import "firebase/compat/firestore";

import { authErrorMessage, needsSignInMethods } from "./authErrors";
import { markVerificationSent, sendVerification } from "./emailVerification";

class FirebaseAuthBackend {
  constructor(firebaseConfig) {
    if (firebaseConfig) {
      // Initialize Firebase
      firebase.initializeApp(firebaseConfig);
      // El email de recuperación de clave y la página donde se elige la clave
      // nueva los arma Firebase: sin esto salen en inglés.
      firebase.auth().languageCode = "es";
      firebase.auth().onAuthStateChanged((user) => {
        if (user) {
          localStorage.setItem("authUser", JSON.stringify(user));
        } else {
          localStorage.removeItem("authUser");
        }
      });
    }
  }

  /**
   * Registers the user with given details
   */
  registerUser = (email, password) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then(
          (user) => {
            const created = firebase.auth().currentUser;
            // Si el envío falla la cuenta igual quedó creada: la pantalla de
            // confirmación lo reintenta y tiene el botón para pedirlo de nuevo.
            sendVerification((settings) => created.sendEmailVerification(settings))
              .then(() => markVerificationSent(created.uid))
              .catch((error) => console.error("[auth]", error?.code, error?.message))
              .then(() => resolve(created));
          },
          (error) => {
            this._explainError(error, "register", email).then(reject);
          }
        );
    });
  };

  /**
   * Registers the user with given details
   */
  editProfileAPI = (email, password) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then(
          (user) => {
            resolve(firebase.auth().currentUser);
          },
          (error) => {
            reject(this._handleError(error));
          }
        );
    });
  };

  /**
   * Login user with given details
   */
  loginUser = (email, password) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signInWithEmailAndPassword(email, password)
        .then(
          (user) => {
            resolve(firebase.auth().currentUser);
          },
          (error) => {
            this._explainError(error, "login", email).then(reject);
          }
        );
    });
  };

  /**
   * forget Password user with given details
   */
  forgetPassword = (email) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .sendPasswordResetEmail(email, {
          url:
            window.location.protocol + "//" + window.location.host + "/login",
        })
        .then(() => {
          resolve(true);
        })
        .catch((error) => {
          reject(this._handleError(error, "reset", { email }));
        });
    });
  };

  /**
   * Logout the user
   */
  logout = () => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signOut()
        .then(() => {
          resolve(true);
        })
        .catch((error) => {
          reject(this._handleError(error));
        });
    });
  };

  /**
   * Social Login user with given details
   */
  // socialLoginUser = (data, type) => {
  //   let credential = {};
  //   if (type === "google") {
  //     credential = firebase.auth.GoogleAuthProvider.credential(data.idToken, data.token);
  //   } else if (type === "facebook") {
  //     credential = firebase.auth.FacebookAuthProvider.credential(data.token);
  //   }
  //   return new Promise((resolve, reject) => {
  //     if (!!credential) {
  //       firebase.auth().signInWithCredential(credential)
  //         .then(user => {
  //           resolve(this.addNewUserToFirestore(user));
  //         })
  //         .catch(error => {
  //           reject(this._handleError(error));
  //         });
  //     } else {
  //       // reject(this._handleError(error));
  //     }
  //   });
  // };

  socialLoginUser = (data, type) => {
    let credential = null;

    if (type === "google") {
      // Usa únicamente el token JWT completo
      credential = firebase.auth.GoogleAuthProvider.credential(data.token);
    } else if (type === "facebook") {
      credential = firebase.auth.FacebookAuthProvider.credential(data.token);
    }

     return new Promise((resolve, reject) => {
       if (credential) {
         firebase
           .auth()
           .signInWithCredential(credential)
           .then((userCredential) => {
             // Ajustar para guardar solo los datos relevantes del usuario
             const user = {
               uid: userCredential.user.uid,
               email: userCredential.user.email,
               displayName: userCredential.user.displayName,
               photoURL: userCredential.user.photoURL,
             };
             localStorage.setItem("authUser", JSON.stringify(user));

             resolve({ user: userCredential, details: user });
           })
           .catch((error) => {
             reject(this._handleError(error, "google"));
           });
       } else {
         reject("No se pudo generar la credencial de inicio de sesión.");
       }
     });
  };


  addNewUserToFirestore = (user) => {
    const collection = firebase.firestore().collection("users");
    const { profile } = user.additionalUserInfo;
    const details = {
      firstName: profile.given_name ? profile.given_name : profile.first_name,
      lastName: profile.family_name ? profile.family_name : profile.last_name,
      fullName: profile.name,
      email: profile.email,
      picture: profile.picture,
      createdDtm: firebase.firestore.FieldValue.serverTimestamp(),
      lastLoginTime: firebase.firestore.FieldValue.serverTimestamp(),
    };
    collection.doc(firebase.auth().currentUser.uid).set(details);
    return { user, details };
  };

  setLoggeedInUser = (user) => {
    localStorage.setItem("authUser", JSON.stringify(user));
  };

  /**
   * Returns the authenticated user
   */
  getAuthenticatedUser = () => {
    if (!localStorage.getItem("authUser")) return null;
    return JSON.parse(localStorage.getItem("authUser"));
  };

  /**
   * Para los errores de email + clave consulta con qué método entra esa
   * cuenta: si se creó con Google no tiene clave, y hay que mandar al usuario
   * al botón de Google en vez de decirle "clave incorrecta".
   */
  _explainError = async (error, context, email) => {
    let methods = [];
    if (needsSignInMethods(error, context)) {
      try {
        methods = await firebase.auth().fetchSignInMethodsForEmail(email);
      } catch (e) {
        // Sin red queda la lista vacía (igual que si el proyecto activara la
        // protección de enumeración) y el mensaje se decide por el dominio
        // del email (@gmail.com).
      }
    }
    return this._handleError(error, context, { email, methods });
  };

  /**
   * Handle the error: devuelve el mensaje en español para el usuario. El
   * detalle técnico de Firebase queda en la consola, para soporte.
   * @param {*} error
   * @param {"login"|"register"|"reset"|"google"} context
   * @param {{email?: string, methods?: string[]}} extra
   */
  _handleError(error, context, extra) {
    console.error("[auth]", error?.code, error?.message);
    return authErrorMessage(error, context, extra);
  }
}

let _fireBaseBackend = null;

/**
 * Initilize the backend
 * @param {*} config
 */
const initFirebaseBackend = config => {
  if (!_fireBaseBackend) {
    _fireBaseBackend = new FirebaseAuthBackend(config);
  }
  return _fireBaseBackend;
};

/**
 * Returns the firebase backend
 */
const getFirebaseBackend = () => {
  return _fireBaseBackend;
};

export { initFirebaseBackend, getFirebaseBackend };
